/**
 * @file CrossDomainBanner.tsx
 * @description Cross-domain banner for navigating between bikeparkmapa.pl and snowparkmap.com
 * Shows at top of pages to inform users about the sister site
 * @module components/CrossDomainBanner
 */

'use client';

import { useEffect, useState } from 'react';
import { Snowflake, Bike, X } from 'lucide-react';

export default function CrossDomainBanner() {
  const [isBikeMapka, setIsBikeMapka] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const hostname = window.location.hostname;
    const searchParams = new URLSearchParams(window.location.search);
    const domainOverride = searchParams.get('domain');

    // Detect domain
    if (domainOverride === 'snowpark') {
      setIsBikeMapka(false);
    } else if (domainOverride === 'bikepark') {
      setIsBikeMapka(true);
    } else {
      setIsBikeMapka(hostname.includes('bikeparkmapa.pl') || hostname.includes('localhost'));
    }

    // Check if user dismissed the banner
    const dismissed = localStorage.getItem('crossDomainBannerDismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
      setIsVisible(false);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('crossDomainBannerDismissed', 'true');
  };

  if (!isVisible || isDismissed) {
    return null;
  }

  return (
    <div className="cross-domain-banner">
      <div className="cross-domain-content">
        {isBikeMapka ? (
          <>
            <span className="cross-domain-icon"><Snowflake size={20} /></span>
            <span className="cross-domain-text">
              Szukasz <strong>snowparków</strong>? Sprawdź{' '}
              <a
                href="https://snowparkmap.com"
                className="cross-domain-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                SnowParkMap.com
              </a>
            </span>
          </>
        ) : (
          <>
            <span className="cross-domain-icon"><Bike size={20} /></span>
            <span className="cross-domain-text">
              Looking for <strong>bikeparks</strong>? Check out{' '}
              <a
                href="https://bikeparkmapa.pl/bikeparks"
                className="cross-domain-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                BikeParkMapa.pl
              </a>
            </span>
          </>
        )}
        <button
          className="cross-domain-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss banner"
        >
          <X size={14} />
        </button>
      </div>

      <style jsx>{`
        .cross-domain-banner {
          background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%);
          border-bottom: 1px solid rgba(59, 130, 246, 0.3);
          padding: 8px 16px;
          position: sticky;
          top: 0;
          z-index: 1000;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .cross-domain-content {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 14px;
          color: #e0f2fe;
        }

        .cross-domain-icon {
          font-size: 20px;
        }

        .cross-domain-text {
          flex: 1;
          line-height: 1.4;
        }

        .cross-domain-text strong {
          color: #ffffff;
          font-weight: 600;
        }

        .cross-domain-link {
          color: #fbbf24;
          font-weight: 600;
          text-decoration: underline;
          transition: color 0.2s;
        }

        .cross-domain-link:hover {
          color: #fcd34d;
        }

        .cross-domain-dismiss {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 4px;
          color: #ffffff;
          cursor: pointer;
          font-size: 14px;
          padding: 4px 8px;
          transition: all 0.2s;
        }

        .cross-domain-dismiss:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        @media (max-width: 768px) {
          .cross-domain-banner {
            padding: 10px 12px;
          }

          .cross-domain-content {
            font-size: 13px;
            gap: 8px;
          }

          .cross-domain-icon {
            font-size: 18px;
          }

          .cross-domain-text {
            line-height: 1.3;
          }
        }
      `}</style>
    </div>
  );
}
